'use client'

import * as React from 'react'

import { cva, VariantProps } from 'class-variance-authority'
import { Avatar as AvatarPrimitive } from 'radix-ui'

import { cn } from '@/utils'

const avatarStatusVariants = cva(
  'flex items-center rounded-full size-2 border-2 border-background',
  {
    defaultVariants: {
      variant: 'online'
    },
    variants: {
      variant: {
        away: 'bg-blue-600',
        busy: 'bg-yellow-600',
        offline: 'bg-zinc-600 dark:bg-zinc-300',
        online: 'bg-green-600'
      }
    }
  }
)

function Avatar({
  children,
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  return (
    <AvatarPrimitive.Root
      className={cn('relative flex size-10 shrink-0 rounded-full', className)}
      data-slot="avatar"
      {...props}
    >
      {children}
    </AvatarPrimitive.Root>
  )
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      className={cn(
        'border-border bg-accent text-accent-foreground flex h-full w-full items-center justify-center rounded-full border text-xs',
        className
      )}
      data-slot="avatar-fallback"
      {...props}
    />
  )
}

function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <div
      className={cn('relative overflow-hidden rounded-[inherit]', className)}
    >
      <AvatarPrimitive.Image
        className={cn('aspect-square h-full w-full object-cover', className)}
        data-slot="avatar-image"
        {...props}
      />
    </div>
  )
}

function AvatarIndicator({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'absolute flex size-6 items-center justify-center',
        className
      )}
      data-slot="avatar-indicator"
      {...props}
    />
  )
}

function AvatarStatus({
  className,
  variant,
  ...props
}: React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof avatarStatusVariants>) {
  return (
    <div
      className={cn(avatarStatusVariants({ variant }), className)}
      data-slot="avatar-status"
      {...props}
    />
  )
}

export {
  Avatar,
  AvatarFallback,
  AvatarImage,
  AvatarIndicator,
  AvatarStatus,
  avatarStatusVariants
}
